// ── Press-Physik (eine Interaktions-Handschrift) ──────────────────────
// Jedes tippbare Element soll sich gleich anfühlen: kurzes „Squish" beim
// Drücken (scale-down) mit der app-weiten Spring-Kurve, das beim Loslassen
// zurückfedert. Das ist der größte „lebendig/teuer"-Hebel — vorher war
// Press-Feedback über die App verstreut (mal CSS :active, mal framer, mal nichts).
//
// Respektiert prefers-reduced-motion (kein Scale). Pointer-Events decken Maus +
// Touch + Pen ab; onPointerLeave/Cancel verhindern „hängende" gedrückte Zustände.

import React, { useState, useCallback } from 'react';
import { EASE, DUR } from '../../styles/theme';

const reduce = () =>
  typeof matchMedia !== 'undefined' &&
  matchMedia('(prefers-reduced-motion: reduce)').matches;

// Hook-Variante: in bestehende <button>-Komponenten einbacken.
//   const { pressTransform, pressHandlers } = usePress({ disabled });
//   <button {...pressHandlers} style={{ …, transform: pressTransform,
//            transition: `transform .14s ${PRESS_EASE}` }} />
export const PRESS_EASE = EASE;

export function usePress({ scale = 0.96, disabled = false } = {}) {
  const [pressed, setPressed] = useState(false);
  const down = useCallback(() => { if (!disabled) setPressed(true); }, [disabled]);
  const up = useCallback(() => setPressed(false), []);
  const active = pressed && !disabled && !reduce();
  return {
    pressed: active,
    pressTransform: active ? `scale(${scale})` : 'scale(1)',
    pressHandlers: {
      onPointerDown: down,
      onPointerUp: up,
      onPointerLeave: up,
      onPointerCancel: up,
    },
  };
}

// Komponenten-Variante: beliebiges Element press-fähig machen, ohne das
// darunterliegende Markup zu ändern (rendert ein <div role=button> bzw. das via
// `as` übergebene Element). Für Karten/Zeilen/Nav-Items.
export function Pressable({
  children, onClick, as: As = 'div', scale = 0.97, disabled = false,
  style = {}, transitionExtra = '', ...rest
}) {
  const { pressTransform, pressHandlers } = usePress({ scale, disabled });
  return (
    <As
      onClick={disabled ? undefined : onClick}
      {...pressHandlers}
      {...rest}
      style={{
        WebkitTapHighlightColor: 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        ...style,
        transform: pressTransform,
        transition: `transform ${DUR.press}ms ${EASE}${transitionExtra ? ', ' + transitionExtra : ''}`,
      }}
    >
      {children}
    </As>
  );
}
